import type { DefaultOptions, UseQueryOptions } from '@tanstack/react-query';

/**
 * Default QueryClient configuration for Commander platform
 * Shared across all apps for consistent behavior
 *
 * Usage:
 * ```typescript
 * import { QueryClient } from '@tanstack/react-query';
 * import { defaultQueryConfig } from '@commander/utils';
 *
 * const queryClient = new QueryClient(defaultQueryConfig);
 * ```
 */
export const defaultQueryConfig = {
  defaultOptions: {
    queries: {
      // Data is considered fresh for 5 minutes
      staleTime: 5 * 60 * 1000,

      // Keep unused data in cache for 10 minutes
      gcTime: 10 * 60 * 1000,

      // Retry failed requests with smart logic
      retry: (failureCount: number, error: any) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        // Retry up to 2 times for 5xx errors
        return failureCount < 2;
      },

      // Exponential backoff for retries
      retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Refetch on window focus for fresh data
      refetchOnWindowFocus: true,

      // Refetch on network reconnect
      refetchOnReconnect: true,

      // Keep cached data on mount — don't refetch just because a component
      // remounted. Mutations that change data refresh the affected queries via
      // invalidateQueries({ refetchType: 'all' }) (see invalidateContactCaches),
      // so data invalidated while unmounted still updates without paying a
      // refetch on every navigation.
      refetchOnMount: false,
    },
    mutations: {
      // Retry once on failure — but never on 4xx, mirroring the query retry
      // above. A 4xx is deterministic (retrying re-POSTs the same failing
      // write), and React Query pauses scheduled retries while the tab is
      // unfocused, so a 4xx that failed in a background tab used to leave the
      // mutation pending — "Saving…" forever with no error — until the tab
      // regained focus (bug logged 2026-08-07, bugs-found.md).
      retry: (failureCount: number, error: any) => {
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        return failureCount < 1;
      },

      // Global error handler for mutations (optional)
      onError: (error: any) => {
        console.error('[Mutation Error]:', error?.message || error);
      },
    },
  } satisfies DefaultOptions,
};

/**
 * Preset query options for different data freshness requirements
 * Use these in individual hooks to override defaults when needed
 */
export const queryOptions = {
  /**
   * Real-time data - 30 seconds stale time
   * Use for: Live dashboards, notifications, real-time feeds
   */
  realtime: {
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 30 * 1000, // Auto-refetch every 30 seconds
  } satisfies Partial<UseQueryOptions>,

  /**
   * Frequently changing data - 2 minutes stale time
   * Use for: Search results, filters, dynamic lists
   */
  fresh: {
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  } satisfies Partial<UseQueryOptions>,

  /**
   * Standard data - 5 minutes stale time (default)
   * Use for: Most lists, standard CRUD operations
   * Note: This is already the default, so you don't need to specify it
   */
  standard: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  } satisfies Partial<UseQueryOptions>,

  /**
   * Stable data - 30 minutes stale time
   * Use for: Reference data, lookups, taxonomies, company settings
   */
  stable: {
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  } satisfies Partial<UseQueryOptions>,

  /**
   * Static data - 1 hour stale time
   * Use for: Rarely changing data like dropdown options, static configs
   */
  static: {
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 24 * 60 * 60 * 1000, // 24 hours
  } satisfies Partial<UseQueryOptions>,

  /**
   * Infinite cache - data never goes stale
   * Use for: Immutable data, historical records, archived content
   */
  infinite: {
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 24 * 60 * 60 * 1000, // 24 hours (still clean up eventually)
  } satisfies Partial<UseQueryOptions>,
} as const;

/**
 * Helper to create custom query options with specific stale time
 *
 * @example
 * ```typescript
 * // Custom 10-minute stale time
 * useQuery({
 *   queryKey: ['data'],
 *   queryFn: fetchData,
 *   ...createQueryOptions({ minutes: 10 })
 * });
 * ```
 */
export function createQueryOptions(params: {
  seconds?: number;
  minutes?: number;
  hours?: number;
}): Partial<UseQueryOptions> {
  const { seconds = 0, minutes = 0, hours = 0 } = params;
  const staleTime = seconds * 1000 + minutes * 60 * 1000 + hours * 60 * 60 * 1000;

  return {
    staleTime,
    gcTime: staleTime * 2, // Keep in cache 2x the stale time
  };
}
